import React, { useEffect, useRef, useState } from 'react';

import { Navigate, useNavigate, useSearchParams } from 'react-router-dom';

import LoadingOverlay from '../../../components/common/LoadingOverlay';
import WaitlistOverlay from '../../../components/common/WaitlistOverlay';
import { showPostInviteMessage, showToast } from '../../../helpers';
import {
  INVITE_ERROR_MESSAGE,
  NAVIGATE_PATH,
} from '../../../helpers/constants';
import { useAppDispatch, useAppSelector } from '../../../hooks/hooks';
import { acceptInvite } from '../../../requests';
import { fetchUser } from '../../../store/reducers/actionCreators';
import { ErrorCode, TInviteType } from '../../../types';

const Invite: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [searchParams] = useSearchParams();
  const sfLearnitCourseId = searchParams.get('sfLearnitCourseId');
  const sfProgramId = searchParams.get('sfProgramId');
  const sfSeriesId = searchParams.get('sfSeriesId');

  const { user, isLoading } = useAppSelector((state) => state.userReducer);
  const hasRunOnce = useRef(false);
  const [isClassFull, setIsClassFull] = useState(false);
  const [inviteData, setInviteData] = useState<{
    inviteType: TInviteType;
    sfLearnitCourseId: string | null;
    sfProgramId: string | null;
    sfSeriesId: string | null;
  } | null>(null);

  useEffect(() => {
    if (
      hasRunOnce.current ||
      (!user && isLoading) ||
      (!sfLearnitCourseId && !sfProgramId && !sfSeriesId)
    ) {
      return;
    }

    hasRunOnce.current = true;

    let inviteType: TInviteType = 'program';
    if (sfLearnitCourseId) {
      inviteType = 'privateClass';
    } else if (sfSeriesId) {
      inviteType = 'classSeries';
    }

    const inviteData = {
      inviteType,
      sfLearnitCourseId,
      sfProgramId,
      sfSeriesId,
    };

    setInviteData(inviteData);

    acceptInvite(inviteData)
      .then(({ resolvedInvite }) => {
        if (resolvedInvite.userPresent) {
          if (resolvedInvite.errorCode === ErrorCode.CLASS_FULL) {
            setIsClassFull(true);
            return;
          }
          // refetch user to show the enrolled program
          dispatch(fetchUser());
          navigate(NAVIGATE_PATH.upcomingClasses);
          showPostInviteMessage(resolvedInvite);
        } else {
          if (resolvedInvite.enforceSaml) {
            navigate(NAVIGATE_PATH.inviteSamlLogin);
          } else {
            navigate(NAVIGATE_PATH.signUp);
          }
        }
      })
      .catch((error) => {
        console.error(error);
        navigate(NAVIGATE_PATH.signUp);
        showToast(INVITE_ERROR_MESSAGE, 'error', { duration: 12000 });
      });
  }, []);

  if (!sfLearnitCourseId && !sfProgramId && !sfSeriesId) {
    showToast(INVITE_ERROR_MESSAGE, 'error');
    return <Navigate to={NAVIGATE_PATH.signUp} />;
  }

  if (isClassFull) {
    return (
      <WaitlistOverlay
        sfLearnitCourseId={inviteData?.sfLearnitCourseId || undefined}
      />
    );
  }

  return (
    <LoadingOverlay
      text={`Please wait while we register you into the ${
        sfLearnitCourseId ? 'class' : sfSeriesId ? 'series' : 'program'
      }. This may take a few seconds.`}
    />
  );
};

export default Invite;
